/*
	This implements a simple search cache for use with the Global reducer and a Rhombus isAsync Select.

	Configuration:
		To configure the hook, call it with an object with the following keys, which are optional, besides `key`,
		but you'll probably need to provide `urlParams` and `transform` for it to work with the API:

			key:
				A string that identifies the type of model you're searching for.  This must correspond to an
				entry in the Global reducer's GLOBAL_ITEMS.

			urlParams:
				A hash of values needed to construct the URL.

				e.g. For assets, the `GLOBAL_ITEMS.assets` object has a `url` function:
					url({ libraryId }){ return `ui/creative-libraries/${libraryId}/assets`; }
				So your `urlParams` should look like:
					{ libraryId: "whateverLibraryId" }

			transform:
				A function that takes the response from the fetch and transforms it into an array of options with
				`name` and `label` properties that the Select component understands.

				See the example usage for how this is handled with the "assets" endpoint.

			debounceTime:
				A number in milliseconds used to debounce the search, so there isn't a search request sent to the API
				for every keystroke.

				Depending on the complexity of your use case, you may want to apply debouncing elsewhere.
				- Because the returned search functions are async, they behave weirdly when debounced, so
				this is only really suitable with an async Select, which uses the `callback` interface.
				- If you need to process the response directly, set the debounceTime to 0.

			cacheLimit:
				A number that denotes the maximum number of transformed payloads a given cache will store.

				If more unique searches are performed than the cacheLimit, the cache will forget them in the order
				they were stored.  First in, first out.


	Return Value:
		`useSearchCache` returns an object with named functions:
			{
				search,
				clear,
			},

		`search` is function that takes three arguments:
			1. an object of queryParams,
			2. a callback, which is the `callback` argument for the `loadOptions` function on the Select component.
			3. a `shouldCache` function that takes the server response and returns whether or not the result should be cached
				- e.g. If the response is an error, you don't want to cache the error, and would like to retry the fetch
				next time, so you can provide a function like:
					response => response.success

		`search` returns a Promise that resolves to an object in the form:
			{
				transformed, // The same result from `search`
				response, // The response before `transform` is applied
			}
			- This is useful if you want to add things like pagination or error handling, and the response includes
			relevant metadata.
			- The full object with `transformed` and `response` is cached, so fetching the result from the server
			or pulling it from the cache will behave consistently, besides the speed.

		`clear` is a function that empties the cache, which you probably won't need to do manually.
		The cache should be garbage collected normally when your component unmounts.

		If you need to share a cache across multiple components, you can call `useSearchCache` in a parent component
		and pass the `search` function to its children.

		When using with an async Select, it's important to not return the result from `search` in the `loadOptions`
		function, or else it will interfere with the `callback` function being used, and will crash the application.

	Example usage:
	```
		function mapAssetToOption(asset){
			return {
				name: asset.name,
				label: asset.name,
				value: asset.id,
			};
		}

		function getAssetOptionsFromPayload(response){
			return response?.payload?.data?.items?.map(mapAssetToOption) || [];
		}

		function AssetSelect({ libraryId, type }){
			let [search] = useSearchCache({
				key: "assets",
				urlParams: { libraryId },
				transform: getAssetOptionsFromPayload
			});

			const defaultSearchParams = {
				rpp: 50,
				sort: "name:asc",
				searchTerm: "",
				advancedFilter: `eq(assetType,${type})`,
			};

			const selectProps = {
				isAsync: true,
				loadOptions: () => {
					search({
						...defaultSearchParams,
						searchTerm: value,
					}, callback);
				}
			}

			return (
				<Select {...selectProps} />
			);
		}
	```


	Implementation notes:
		- `loadOptions` can alternatively return a Promise, but it's difficult to make that mesh with debouncing.
		- react-select, the underlying component in Rhombus's Select, has a `cacheOptions` prop, but it doesn't
		allow us to share a cache.
*/

import { useRef } from "react";
import { useDispatch } from "react-redux";
import { Global } from "reducers/Global";
import { debounce } from "lodash";

import { noop } from "utils/common";
import { SEARCH_DEBOUNCE } from "utils/constants";

const CACHE_LIMIT = 10;

function sortedStringify(obj){
	/*
		JSON.stringify will order its properties in the order that they were added, which means
		that equivalent objects won't always turn into the same string.

		e.g.:
		```
			let obj1 = {};
			obj1.a = "a";
			obj1.b = "b";

			let obj2 = {};
			obj2.b = "b";
			obj2.a = "a";

			JSON.stringify(obj1); // '{"a":"a","b":"b"}'
			JSON.stringify(obj2); // '{"b":"b","a":"a"}'
		```

		To get a hash of searchParams to be a suitable cache key, we have to make the order consistent
		by sorting the keys with the optional second argument for JSON.stringify.
	*/
	return JSON.stringify(obj, Object.keys(obj).sort());
}

function identityTransform(item){
	return item;
}

function cacheOnSuccess(response){
	// Our AJAX middleware fairly reliably provides this property
	return response?.success;
}

export default function useSearchCache({
	key,
	urlParams = {},
	transform = identityTransform,
	debounceTime = SEARCH_DEBOUNCE,
	cacheLimit = CACHE_LIMIT,
}){
	const dispatch = useDispatch();
	const cacheRef = useRef(new Map());
	const cache = cacheRef.current;

	function addToCache(cacheKey, item){
		if(cache.size >= cacheLimit){
			// cache is too big, so remove the oldest item before adding a new one
			let [firstKey] = cache.keys();
			cache.delete(firstKey);
		}
		cache.set(cacheKey, item);
	}

	function search(queryParams, callback = noop, shouldCache = cacheOnSuccess){
		let cacheKey = sortedStringify(queryParams);

		if(cache.has(cacheKey)){
			let cached = cache.get(cacheKey);
			callback(cached.transformed);
			return Promise.resolve(cached);
		}

		// Cache missed, so dispatch the search.
		return dispatch(Global.search(key, urlParams, queryParams)).then((response) => {
			let result = transform(response);

			if(shouldCache(response)){
				addToCache(cacheKey, {
					transformed: result,
					response,
				});
				callback(result);
			}

			return {
				transformed: result,
				response,
			};
		});
	}

	function clear(){
		cache.clear();
	}

	let searchFn = (debounceTime > 0) ? debounce(search, debounceTime) : search;

	return {
		search: searchFn,
		clear,
	};
}
